import { useState } from 'react';

/**
 * LazyImage — drop-in replacement for `<img>`:
 *   - native `loading="lazy"` (browser-level intersection observer)
 *   - native `decoding="async"`
 *   - low-quality blurred placeholder while the real image loads
 *
 * The placeholder is a tiny inline SVG (no extra request, no CSS). It blurs
 * out as soon as `onLoad` fires, giving a smooth blur-up effect on slow
 * connections without shipping a new dependency.
 *
 * Use in feed cards, carousels, anywhere a remote image lives below the fold.
 */
const PLACEHOLDER_SVG =
  // Neutral 8×6 placeholder using design-system surface tokens (#F3F4F6 → #E5E7EB).
  // Blurs out cleanly when the real image loads. Inlined as base64 — zero network.
  'data:image/svg+xml;base64,' +
  btoa(`<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 8 6">
    <defs>
      <linearGradient id="g" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stop-color="#F3F4F6"/>
        <stop offset="100%" stop-color="#E5E7EB"/>
      </linearGradient>
    </defs>
    <rect width="8" height="6" fill="url(#g)"/>
  </svg>`);

export const LazyImage = ({
  src,
  alt = '',
  className = '',
  placeholder = PLACEHOLDER_SVG,
  onClick,
  // Accept arbitrary HTML img attributes (sizes, srcset, data-testid…)
  ...rest
}) => {
  const [loaded, setLoaded] = useState(false);

  return (
    <span
      className={`relative block w-full h-full overflow-hidden ${className}`}
      onClick={onClick}
    >
      {/* Blurred placeholder — sits behind the real image. Painted instantly. */}
      <img
        src={placeholder}
        alt=""
        aria-hidden="true"
        className={`absolute inset-0 w-full h-full object-cover blur-lg scale-110 transition-opacity duration-300 ${
          loaded ? 'opacity-0' : 'opacity-100'
        }`}
      />
      <img
        src={src}
        alt={alt}
        loading="lazy"
        decoding="async"
        onLoad={() => setLoaded(true)}
        // Same dimensions/positioning as the placeholder, but on top.
        className={`relative w-full h-full object-cover transition-opacity duration-500 ${
          loaded ? 'opacity-100' : 'opacity-0'
        }`}
        {...rest}
      />
    </span>
  );
};
